import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users2, Plus, Eye, CalendarDays, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtRangeLong } from "@/pages/cotizador/shared";
import { CURATED_STATUS, fmtMoney } from "@/pages/curated/shared";
import CuratedDetail from "@/pages/curated/CuratedDetail";
import CuratedStatsOverview from "@/pages/curated/CuratedStatsOverview";

export default function CuratedPage() {
  const [sp, setSp] = useSearchParams();
  const selected = sp.get("id");
  const view = sp.get("view") || "list";
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: items, isLoading } = useQuery({ queryKey: ["curated"], queryFn: async () => (await api.get("/curated")).data });

  const open = (id, tab) => { const n = new URLSearchParams(); n.set("id", id); if (tab) n.set("tab", tab); setSp(n); };
  const back = () => { setSp(new URLSearchParams()); qc.invalidateQueries({ queryKey: ["curated"] }); };

  if (selected) return <CuratedDetail id={selected} onBack={back} />;

  return (
    <div className="space-y-6" data-testid="curated-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight"><Users2 className="h-6 w-6 text-primary" /> Curated Trips</h1>
          <p className="text-sm text-muted-foreground">Viajes en grupo con fecha fija, cupo y precio por persona. Cada viaje tiene su landing pública y reserva con hold de 10 minutos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === "stats" ? "default" : "outline"} onClick={() => { const n = new URLSearchParams(); if (view !== "stats") n.set("view", "stats"); setSp(n); }} data-testid="curated-stats-toggle">
            <BarChart3 className="mr-1.5 h-4 w-4" /> Estadísticas
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="curated-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Nuevo Curated Trip</Button>
        </div>
      </div>

      {view === "stats" ? <CuratedStatsOverview onOpen={open} /> : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              : !items?.length ? (
                <div className="p-12 text-center text-sm text-muted-foreground" data-testid="curated-empty">
                  Aún no hay Curated Trips. Crea el primero con «Nuevo Curated Trip».
                </div>
              ) : (
                <Table data-testid="curated-table">
                  <TableHeader><TableRow>
                    <TableHead>Viaje</TableHead><TableHead>Fechas</TableHead><TableHead>Plazas</TableHead>
                    <TableHead>Precio / persona</TableHead><TableHead>Reservas</TableHead><TableHead>Visitas 30d</TableHead><TableHead>Estado</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {items.map((t) => {
                      const st = CURATED_STATUS[t.status] || CURATED_STATUS.draft;
                      return (
                        <TableRow key={t.id} className="cursor-pointer" onClick={() => open(t.id)} data-testid={`curated-row-${t.id}`}>
                          <TableCell>
                            <div className="font-medium">{t.name}</div>
                            <div className="text-xs text-muted-foreground">{t.destination || "—"}{t.created_by ? ` · ${t.created_by}` : ""}</div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm"><CalendarDays className="mr-1 inline h-3.5 w-3.5 text-muted-foreground" />{t.start_date ? fmtRangeLong(t.start_date, t.end_date) : "Sin fechas"}</TableCell>
                          <TableCell className="text-sm tabular-nums">
                            <span className="font-medium">{t.seats_committed}</span> / {t.capacity}
                            <span className="ml-1 text-xs text-muted-foreground">({t.seats_available} libres)</span>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums">{fmtMoney(t.price_double, t.currency)} <span className="text-xs text-muted-foreground">doble</span> · {fmtMoney(t.price_single, t.currency)} <span className="text-xs text-muted-foreground">single</span></TableCell>
                          <TableCell className="text-sm tabular-nums">{t.paid_count} pagadas <span className="text-xs text-muted-foreground">/ {t.bookings_count}</span><div className="text-xs text-emerald-700">{t.revenue > 0 ? fmtMoney(t.revenue, t.currency) : ""}</div></TableCell>
                          <TableCell className="text-sm tabular-nums"><Eye className="mr-1 inline h-3.5 w-3.5 text-muted-foreground" />{t.stats?.views_30d ?? 0}</TableCell>
                          <TableCell><Badge variant={st.variant} data-testid={`curated-status-${t.id}`}>{st.label}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>
      )}

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(t) => { setCreateOpen(false); qc.invalidateQueries({ queryKey: ["curated"] }); open(t.id); }} />
    </div>
  );
}

function CreateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", destination: "", capacity: 12, price_single: "", price_double: "", currency: "USD", start_date: "", end_date: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const m = useMutation({
    mutationFn: async () => (await api.post("/curated", {
      ...form, capacity: Number(form.capacity) || 12, price_single: Number(form.price_single) || 0, price_double: Number(form.price_double) || 0,
      start_date: form.start_date || null, end_date: form.end_date || null,
    })).data,
    onSuccess: (t) => { toast.success("Curated Trip creado"); onCreated(t); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="curated-create-dialog">
        <DialogHeader><DialogTitle>Nuevo Curated Trip</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Piedmont & Lake Como · October 2026" data-testid="curated-create-name" /></div>
          <div><Label>Destino</Label><Input value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Italy" data-testid="curated-create-destination" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Inicio</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="curated-create-start" /></div>
            <div><Label>Fin</Label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="curated-create-end" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Cupo</Label><Input type="number" min={1} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="curated-create-capacity" /></div>
            <div><Label>Moneda</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}><SelectTrigger data-testid="curated-create-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USD">USD ($)</SelectItem><SelectItem value="EUR">EUR (€)</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Precio single / persona</Label><Input type="number" min={0} value={form.price_single} onChange={(e) => set("price_single", e.target.value)} data-testid="curated-create-single" /></div>
            <div><Label>Precio doble / persona</Label><Input type="number" min={0} value={form.price_double} onChange={(e) => set("price_double", e.target.value)} data-testid="curated-create-double" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => m.mutate()} disabled={!form.name.trim() || m.isPending} data-testid="curated-create-submit">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
